/**
 * ParentSettings — Phase 7A overhaul.
 *
 * Tabs (left rail on desktop, segmented bar on mobile):
 *   1. Profile             — every editable field, validated
 *   2. Linked Children     — read-only cards listing the parent's
 *                            children with branch/class/admission info
 *   3. Notifications       — channel toggles (email / WA / SMS / in-app)
 *   4. Account & Security  — change-password trigger, account status
 *
 * Backend contract:
 *   PATCH /api/auth/me                   — self-service profile update
 *   GET   /api/auth/me/linked-children   — children with branch hydrated
 *
 * Strict allow-list on the backend means admin-only fields (role,
 * branch_id, franchise_id, …) are rejected with a 400 — the UI never
 * even surfaces them so this is defence-in-depth, not the primary gate.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  User, Bell, ShieldCheck, Baby, Buildings, EnvelopeSimple, Phone,
  ChatTeardropDots, FloppyDisk, X,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Panel, Pill, Skeleton } from "@/components/Bits";
import PhoneInput from "@/components/PhoneInput";
import { NameInput } from "@/lib/validators";
import VerifyOtpModal from "@/components/VerifyOtpModal";

const SENSITIVE_FIELDS = new Set([
  "phone", "whatsapp_phone", "alt_email",
  "emergency_contact", "pickup_permission",
]);

const TABS = [
  { key: "profile",   label: "Profile",            icon: User },
  { key: "children",  label: "Linked children",    icon: Baby },
  { key: "notify",    label: "Notifications",      icon: Bell },
  { key: "security",  label: "Account & security", icon: ShieldCheck },
];

const FAMILY_ROLES = new Set(["father", "mother", "guardian", "parent"]);
const GUARDIAN_TYPES = [
  "", "grandfather", "grandmother", "uncle", "aunt", "elder_sibling",
  "legal_guardian", "other",
];

export default function ParentSettings() {
  const { user, refresh: refreshUser } = useAuth();
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState(null);
  const [children, setChildren] = useState([]);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [phoneValid, setPhoneValid] = useState(true);
  const [waPhoneValid, setWaPhoneValid] = useState(true);
  const [otpModal, setOtpModal] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      api.get("/auth/me"),
      api.get("/auth/me/linked-children"),
    ]).then(([meRes, kidsRes]) => {
      const p = meRes.value?.data || user;
      setProfile(p);
      setDraft(p);
      setChildren(kidsRes.value?.data || []);
    });
  }, [user]);

  const dirty = useMemo(() => {
    if (!profile) return false;
    return Object.keys(draft).some(
      (k) => (draft[k] ?? null) !== (profile[k] ?? null)
    );
  }, [draft, profile]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    // Mandatory phone must be valid. WhatsApp is optional — only
    // validate it when the user has actually filled something in.
    if (!phoneValid) {
      toast.error("Mobile number isn't valid yet. Please complete it before saving.");
      return;
    }
    if (draft.whatsapp_phone && !waPhoneValid) {
      toast.error("WhatsApp number isn't valid. Clear it or fix the format.");
      return;
    }
    // Compute diff so we only ship changed fields — keeps the audit log
    // clean and avoids re-validating untouched fields.
    const patch = {};
    Object.keys(draft).forEach((k) => {
      if ((draft[k] ?? null) === (profile[k] ?? null)) return;
      // Strip admin-only / non-editable fields client-side too — the
      // backend allow-list is the authoritative gate but echoing them
      // back in the diff would just cause noisy 400s.
      if (["role", "branch_id", "branch_ids", "franchise_id",
            "managed_branch_ids", "permissions", "is_active",
            "email", "user_id", "_id", "password_hash",
            "modified_at", "modified_by", "created_at"].includes(k)) return;
      patch[k] = draft[k];
    });
    if (Object.keys(patch).length === 0) {
      toast.info("Nothing to save.");
      return;
    }
    // Phase 7C — gate sensitive fields behind an OTP confirmation.
    const sensitiveChanged = Object.keys(patch).some((k) => SENSITIVE_FIELDS.has(k));
    if (sensitiveChanged) {
      setOtpModal({
        identifier: profile.email,
        channel: "email",
        title: "Confirm sensitive profile change",
        body: "We've sent a 6-digit code to your email. Enter it to save the changes to your contact / pickup details.",
        onConfirmed: async (code) => { await persist(patch, code); },
      });
      return;
    }
    await persist(patch);
  };

  const persist = async (patch, otpCode) => {
    setSaving(true);
    try {
      // Strip empty-string phone fields before POSTing — saves the
      // backend a useless validation round on a "" value that the
      // E.164 helper would reject. We treat empty as "clear this field"
      // which the patch endpoint already supports via $set: null.
      const final = { ...patch };
      if (final.whatsapp_phone === "") final.whatsapp_phone = null;
      if (otpCode) final._otp_code = otpCode;
      const { data } = await api.patch("/auth/me", final);
      setProfile(data);
      setDraft(data);
      setOtpModal(null);
      toast.success("Profile updated.");
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not save profile."
      );
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setDraft(profile); toast.info("Changes reverted."); };

  if (!profile) {
    return <Skeleton lines={6} />;
  }

  const isFamily = FAMILY_ROLES.has(profile.role);

  return (
    <div className="pb-24 lg:pb-6" data-testid="parent-settings-page">
      <PageHeader
        eyebrow="Settings"
        title="Your profile"
        subtitle={
          profile.last_profile_update
            ? `Last updated ${new Date(profile.last_profile_update).toLocaleString()}`
            : "Keep your details current so the school can reach you fast."
        }
      />

      {/* Tab strip */}
      <div className="inline-flex rounded-full bg-paper-alt p-1 border border-border mb-6 overflow-x-auto max-w-full" data-testid="settings-tabs">
        {TABS.map((t) => {
          const Ic = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 transition ${tab === t.key ? "bg-white shadow-sm text-ink" : "text-ink-soft hover:text-ink"}`}
            >
              <Ic size={14} weight="duotone" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "profile" && (
        <ProfileTab
          profile={profile}
          draft={draft}
          setField={setField}
          isFamily={isFamily}
          phoneValid={phoneValid}
          setPhoneValid={setPhoneValid}
          waPhoneValid={waPhoneValid}
          setWaPhoneValid={setWaPhoneValid}
        />
      )}

      {tab === "children" && <ChildrenTab children={children} isFamily={isFamily} />}

      {tab === "notify" && (
        <NotifyTab draft={draft} setField={setField} />
      )}

      {tab === "security" && (
        <SecurityTab profile={profile} />
      )}

      {/* Sticky save bar — appears only when there's something to save and we're on a writable tab */}
      {dirty && ["profile", "notify"].includes(tab) && (
        <div className="fixed bottom-0 inset-x-0 lg:inset-x-auto lg:right-6 lg:bottom-6 lg:left-auto lg:w-auto z-30" data-testid="save-bar">
          <div className="bg-ink text-paper rounded-none lg:rounded-2xl px-5 py-3 flex items-center gap-3 shadow-2xl">
            <span className="text-sm flex-1 lg:flex-none">Unsaved changes</span>
            <button onClick={cancel}
              className="px-3 h-9 rounded-full border border-paper/30 text-sm" data-testid="cancel-btn">
              <X weight="bold" /> Cancel
            </button>
            <button onClick={save} disabled={saving}
              className="px-4 h-9 rounded-full bg-mustard text-ink font-semibold inline-flex items-center gap-1.5 text-sm disabled:opacity-50" data-testid="save-btn">
              <FloppyDisk weight="bold" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      {otpModal && (
        <VerifyOtpModal
          identifier={otpModal.identifier}
          channel={otpModal.channel}
          title={otpModal.title}
          body={otpModal.body}
          onCancel={() => setOtpModal(null)}
          onConfirmed={otpModal.onConfirmed}
        />
      )}
    </div>
  );
}

/* ───────────────────────── Profile tab ─────────────────────────── */
function ProfileTab({ profile, draft, setField, isFamily, phoneValid, setPhoneValid, waPhoneValid, setWaPhoneValid }) {
  return (
    <div className="space-y-4" data-testid="profile-tab">
      <Panel title="Identity">
        <div className="flex items-start gap-4 mb-4">
          <PhotoUpload value={draft.photo_url} onChange={(v) => setField("photo_url", v)} />
          <div className="flex-1">
            <Pill tone="mustard">{(profile.role || "parent").replace(/_/g, " ")}</Pill>
            <div className="text-xs text-ink-soft mt-2">Adding a photo helps the school recognise you at pickup. JPG/PNG, ≤2 MB.</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Full name" required>
            <NameInput
              value={draft.name || ""}
              onChange={(v) => setField("name", v)}
              placeholder="e.g. Priya Sharma"
              testid="profile-name"
            />
          </Field>
          <Field label="Email (login)">
            {/* Email is intentionally read-only here — changing it would
                break session/OTP routing. Admins handle email changes. */}
            <ReadOnly value={profile.email} testid="profile-email-readonly" />
          </Field>
          {isFamily && (
            <Field label="Relationship to child">
              <select
                className="input"
                value={draft.relationship || ""}
                onChange={(e) => setField("relationship", e.target.value)}
                data-testid="profile-relationship"
              >
                <option value="">Select…</option>
                <option value="Father">Father</option>
                <option value="Mother">Mother</option>
                <option value="Guardian">Guardian</option>
              </select>
            </Field>
          )}
          {isFamily && (draft.relationship === "Guardian" || draft.guardian_type) && (
            <Field label="Guardian type">
              <select
                className="input"
                value={draft.guardian_type || ""}
                onChange={(e) => setField("guardian_type", e.target.value)}
                data-testid="profile-guardian-type"
              >
                {GUARDIAN_TYPES.map((g) => (
                  <option key={g} value={g}>{g ? g.replace("_", " ") : "Select…"}</option>
                ))}
              </select>
            </Field>
          )}
        </div>
      </Panel>

      <Panel title="Contact">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Mobile number" required>
            <PhoneInput
              value={draft.phone || ""}
              onChange={(v) => setField("phone", v)}
              onValidityChange={setPhoneValid}
              placeholder="9844139333"
              testId="profile-phone"
            />
          </Field>
          <Field label="WhatsApp (if different)">
            <PhoneInput
              value={draft.whatsapp_phone || ""}
              onChange={(v) => setField("whatsapp_phone", v)}
              onValidityChange={setWaPhoneValid}
              placeholder="9844139333"
              testId="profile-whatsapp"
            />
          </Field>
          <Field label="Alternate email">
            <input
              type="email"
              className="input"
              value={draft.alt_email || ""}
              onChange={(e) => setField("alt_email", e.target.value)}
              placeholder="alt@example.com"
              data-testid="profile-alt-email"
            />
          </Field>
        </div>
      </Panel>

      <Panel title="Professional">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Occupation">
            <input
              className="input"
              value={draft.occupation || ""}
              onChange={(e) => setField("occupation", e.target.value)}
              placeholder="e.g. Software Engineer"
              data-testid="profile-occupation"
            />
          </Field>
          <Field label="Employer / company (optional)">
            <input
              className="input"
              value={draft.employer || ""}
              onChange={(e) => setField("employer", e.target.value)}
              placeholder="e.g. Factskoot Edutech"
              data-testid="profile-employer"
            />
          </Field>
        </div>
      </Panel>

      <Panel title="Address">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Residential address" className="sm:col-span-2">
            <textarea
              rows={2}
              className="input"
              value={draft.address || ""}
              onChange={(e) => setField("address", e.target.value)}
              placeholder="e.g. 42, 5th Cross, Indiranagar 2nd Stage"
              data-testid="profile-address"
            />
          </Field>
          <Field label="City">
            <input className="input"
              value={draft.city || ""}
              onChange={(e) => setField("city", e.target.value)}
              placeholder="Bangalore" data-testid="profile-city" />
          </Field>
          <Field label="State">
            <input className="input"
              value={draft.state || ""}
              onChange={(e) => setField("state", e.target.value)}
              placeholder="Karnataka" data-testid="profile-state" />
          </Field>
          <Field label="Country">
            <input className="input"
              value={draft.country || "India"}
              onChange={(e) => setField("country", e.target.value)}
              placeholder="India" data-testid="profile-country" />
          </Field>
          <Field label="Pincode / Postal code">
            <input className="input"
              value={draft.pincode || ""}
              onChange={(e) => setField("pincode", e.target.value)}
              placeholder="560038" data-testid="profile-pincode" />
          </Field>
        </div>
      </Panel>

      {isFamily && (
        <Panel title="Pickup & emergency">
          <div className="grid sm:grid-cols-2 gap-4">
            <Toggle
              label="I'm an emergency contact for my child"
              checked={!!draft.emergency_contact}
              onChange={(v) => setField("emergency_contact", v)}
              testid="profile-emergency"
            />
            <Toggle
              label="I'm authorised to pick up / drop off"
              checked={!!draft.pickup_permission}
              onChange={(v) => setField("pickup_permission", v)}
              testid="profile-pickup"
            />
          </div>
          <p className="text-xs text-ink-soft mt-3">
            Changes to pickup / emergency status are visible to your child's
            branch admin and class teacher immediately.
          </p>
        </Panel>
      )}
    </div>
  );
}

/* ─────────────────────── Linked Children tab ───────────────────── */
function ChildrenTab({ children, isFamily }) {
  if (!isFamily) {
    return (
      <Panel title="Linked children">
        <Empty label="This tab is only visible to parents and guardians." />
      </Panel>
    );
  }
  if (children.length === 0) {
    return (
      <Panel title="Linked children">
        <Empty label="No children linked to your account yet. If you've recently enrolled, please contact your branch admin." />
      </Panel>
    );
  }
  return (
    <div className="space-y-4" data-testid="children-tab">
      {children.map((s) => (
        <Panel key={s.student_id} testid={`child-${s.student_id}`}>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-mustard/30 flex items-center justify-center text-2xl font-heading font-bold text-mustard-deep">
              {(s.name || "?")[0]}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-heading text-lg font-bold">{s.name}</div>
                {s.admission_number && <Pill tone="sage">{s.admission_number}</Pill>}
                {s.status && <Pill tone={s.status === "active" ? "sage" : "ink"}>{s.status}</Pill>}
              </div>
              <div className="text-sm text-ink-soft mt-1 flex items-center gap-1">
                <Buildings size={14} weight="duotone" />
                {s.branch_name || s.branch_id}
                {s.branch_city && <> · {s.branch_city}</>}
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mt-3 text-sm">
                <Detail label="Student ID" value={s.student_id} />
                <Detail label="Class / batch" value={s.classroom || s.class_name || "—"} />
                <Detail label="Date of birth" value={(s.dob || "").slice(0, 10) || "—"} />
                <Detail label="Programme" value={s.program_type || s.enrolment_type || "—"} />
                <Detail label="Gender" value={s.gender || "—"} />
                <Detail label="Blood group" value={s.blood_group || "—"} />
              </div>
            </div>
          </div>
        </Panel>
      ))}
    </div>
  );
}

/* ───────────────────────── Notifications tab ───────────────────── */
function NotifyTab({ draft, setField }) {
  return (
    <Panel title="How should we reach you?" testid="notify-tab">
      <p className="text-sm text-ink-soft mb-4">
        Switch the channels you'd like to receive school announcements,
        payment receipts, and pickup alerts on.
      </p>
      <div className="space-y-3">
        <NotifyRow icon={<EnvelopeSimple size={18} weight="duotone" />} label="Email"
          checked={draft.notify_email !== false}
          onChange={(v) => setField("notify_email", v)} testid="notify-email" />
        <NotifyRow icon={<ChatTeardropDots size={18} weight="duotone" />} label="WhatsApp"
          checked={draft.notify_whatsapp !== false}
          onChange={(v) => setField("notify_whatsapp", v)} testid="notify-whatsapp" />
        <NotifyRow icon={<Phone size={18} weight="duotone" />} label="SMS"
          checked={!!draft.notify_sms}
          onChange={(v) => setField("notify_sms", v)} testid="notify-sms" />
        <NotifyRow icon={<Bell size={18} weight="duotone" />} label="In-app push"
          checked={draft.notify_in_app !== false}
          onChange={(v) => setField("notify_in_app", v)} testid="notify-inapp" />
      </div>
      <div className="mt-6">
        <Field label="Preferred language">
          <select className="input"
            value={draft.language || "en"}
            onChange={(e) => setField("language", e.target.value)}
            data-testid="notify-language">
            <option value="en">English</option>
            <option value="hi">Hindi</option>
            <option value="kn">Kannada</option>
            <option value="te">Telugu</option>
            <option value="ta">Tamil</option>
            <option value="mr">Marathi</option>
          </select>
        </Field>
      </div>
    </Panel>
  );
}

/* ────────────────────── Account & Security tab ─────────────────── */
function SecurityTab({ profile }) {
  const [resetting, setResetting] = useState(false);

  const sendReset = async () => {
    setResetting(true);
    try {
      await api.post("/auth/forgot-password", {
        identifier: profile.email,
        channel: "email",
      });
      toast.success("Password reset link sent to your email.");
    } catch {
      toast.error("Could not send reset link.");
    } finally { setResetting(false); }
  };

  return (
    <div className="space-y-4" data-testid="security-tab">
      <Panel title="Account">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <Detail label="Login email" value={profile.email} />
          <Detail label="Role" value={profile.role} />
          <Detail label="Status" value={profile.is_active === false ? "Deactivated" : "Active"} />
          <Detail label="Last login" value={profile.last_login ? new Date(profile.last_login).toLocaleString() : "—"} />
        </div>
      </Panel>
      <Panel title="Password">
        <p className="text-sm text-ink-soft mb-3">
          For security we send password resets via email. Click below to
          receive a secure link.
        </p>
        <button
          onClick={sendReset}
          disabled={resetting}
          className="px-5 h-11 rounded-full bg-terracotta text-white font-semibold disabled:opacity-50"
          data-testid="password-reset-btn"
        >
          {resetting ? "Sending…" : "Send password reset link"}
        </button>
      </Panel>
      <Panel title="Privacy & consent">
        <p className="text-sm text-ink-soft">
          MangoCubs follows the&nbsp;
          <a href="/privacy" className="text-terracotta underline">privacy policy</a>.
          We never share your data with third parties without your consent.
        </p>
      </Panel>
    </div>
  );
}

/* ───────────────────────── micro components ────────────────────── */
function PhotoUpload({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Photo must be ≤ 2 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload?folder=profile_photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url || data.file_url);
      toast.success("Photo uploaded — click Save to apply.");
    } catch { toast.error("Could not upload photo."); }
    finally { setBusy(false); }
  };
  return (
    <label className="relative inline-block cursor-pointer">
      {value ? (
        <img src={value} alt="" className="w-20 h-20 rounded-2xl object-cover border-2 border-mustard" />
      ) : (
        <div className="w-20 h-20 rounded-2xl bg-paper-alt border-2 border-dashed border-border flex items-center justify-center text-ink-soft text-xs">+ Photo</div>
      )}
      <input type="file" accept="image/png,image/jpeg" hidden onChange={onPick} data-testid="profile-photo-upload" />
      {busy && <div className="absolute inset-0 rounded-2xl bg-white/70 flex items-center justify-center text-xs">…</div>}
    </label>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs text-ink-soft block mb-1">
        {label} {required && <span className="text-terracotta">*</span>}
      </span>
      {children}
      <style>{`
        .input { width: 100%; height: 44px; padding: 0 12px; border-radius: 12px;
                 border: 1px solid #EBE5DF; background: #fff; outline: none;
                 font-family: 'DM Sans', sans-serif; font-size: 14px; }
        .input:focus { box-shadow: 0 0 0 2px rgba(229,107,85,0.4); }
        .input::placeholder { color: #BDB3A7; font-weight: 400; opacity: 1; }
        textarea.input { padding: 10px 12px; height: auto; }
      `}</style>
    </label>
  );
}

function ReadOnly({ value, testid }) {
  return (
    <div className="h-11 px-3 rounded-xl border border-border bg-paper-alt/60 flex items-center text-sm text-ink-soft" data-testid={testid}>
      {value} <span className="ml-2 text-xs">· read-only</span>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 p-3 rounded-xl border border-border cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 accent-terracotta" data-testid={testid} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

function NotifyRow({ icon, label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 p-3 rounded-xl border border-border cursor-pointer hover:bg-paper-alt/50 transition">
      <span className="text-terracotta">{icon}</span>
      <span className="flex-1 text-sm font-medium">{label}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 accent-terracotta" data-testid={testid} />
    </label>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-soft mb-0.5">{label}</div>
      <div className="font-medium break-words">{value}</div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="py-12 text-center text-sm text-ink-soft">{label}</div>;
}
